import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState, type ReactElement } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import {
	LiveUpdateService,
	type LiveUpdateStateModel,
} from '../../services/LiveUpdateService';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';

interface LiveUpdateBannerProps {
	/**
	 * crm-api base (VITE_API_URL), bare origin or origin + `/api`; the
	 * live-update endpoint is public and tenantless.
	 */
	apiBaseUrl: string;
}

/**
 * PD-13933 — the native shells' live-update notice. Mounted once in each
 * app shell beside NativeUpdateGate (store updates) and AppUpdateBanner
 * (browser updates, inert on native); it renders nothing and calls nothing
 * outside a native shell.
 *
 * Mounting starts LiveUpdateService, which reports the running bundle as
 * ready to the on-device updater straight away, so this component should
 * mount as early in the tree as the other two.
 *
 * Shown only once a verified update is staged. Dismissing hides it for the
 * session; the update still applies on the next cold launch. Colors match
 * AppUpdateBanner, the browser notice for the same event.
 */
export function LiveUpdateBanner({ apiBaseUrl }: LiveUpdateBannerProps): ReactElement | null {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [state, setState] = useState<LiveUpdateStateModel>(LiveUpdateService.getState());
	const [dismissedId, setDismissedId] = useState<string | null>(null);

	useEffect(() => {
		const unsubscribe = LiveUpdateService.subscribe(setState);
		void LiveUpdateService.start({ apiBaseUrl });
		return unsubscribe;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (state.readyBundleId == null || state.readyBundleId === dismissedId) {
		return null;
	}

	// A floating card rather than an in-flow strip: both shells' headers
	// already pad for the status bar, so a strip above them doubled that
	// gap. The card overlaps the header only until it is dismissed or used.
	return (
		<div
			role="status"
			className={cn(
				'fixed inset-x-3 z-[9999] flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm shadow-lg border',
				colors.classes.borderDefault,
			)}
			style={{
				top: 'calc(env(safe-area-inset-top) + 0.5rem)',
				backgroundColor: colors.values.primaryDark,
				color: '#ffffff',
			}}
		>
			<Icon icon={faArrowsRotate} />
			<span className="flex-1 min-w-0">
				{state.applyPending ? t('Updating…') : t('An update is ready.')}
			</span>
			{!state.applyPending && (
				<>
					<button
						type="button"
						onClick={() => {
							LiveUpdateService.applyNow();
						}}
						className="underline font-semibold whitespace-nowrap"
					>
						{t('Restart now')}
					</button>
					<button
						type="button"
						aria-label={t('Dismiss')}
						onClick={() => {
							setDismissedId(state.readyBundleId);
						}}
						className="text-lg leading-none px-1"
					>
						&times;
					</button>
				</>
			)}
		</div>
	);
}
